// WP-1.12 (SR-1, S-37 `/search`): single input; results grouped by kind with match
// highlighting; amount-aware (the engine's querySearch switches into amount-matching mode
// for a bare/≈-prefixed amount like "50000" or "≈500 usd" — this page just displays whatever
// comes back). The `q` URL param makes a search shareable/bookmarkable and is how the
// TopBar's `/`-shortcut input hands off here.
import type { SearchResult } from '@pfos/shared';
import { Search as SearchIcon } from 'lucide-react';
import { useState } from 'react';
import { type NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { inputClass } from '../components/FormKit';
import { useSearch } from '../lib/search';

const GROUP_LABELS: Record<SearchResult['kind'], string> = {
  transaction: 'Transactions',
  transfer: 'Transfers',
  rp: 'Owed (R&P)',
  tpf: 'Third-party',
  account: 'Accounts',
  recurring_template: 'Recurring',
};

const GROUP_ORDER: readonly SearchResult['kind'][] = [
  'transaction',
  'transfer',
  'rp',
  'tpf',
  'account',
  'recurring_template',
];

function resultHref(result: SearchResult): string {
  switch (result.kind) {
    case 'transaction':
      return `/tx/${result.id}`;
    case 'transfer':
      return '/tx';
    case 'rp':
      return '/owed';
    case 'tpf':
      return `/tpf/${result.id}`;
    case 'account':
      return `/accounts/${result.id}`;
    case 'recurring_template':
      return '/recurring';
  }
}

function Highlight({
  text,
  query,
  testId,
}: {
  text: string;
  query: string;
  testId: string;
}): React.JSX.Element {
  const q = query.trim();
  const idx = q === '' ? -1 : text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark data-testid={testId} className="rounded-sm bg-primary/20 text-ink">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function ResultRow({
  result,
  query,
  navigate,
}: {
  result: SearchResult;
  query: string;
  navigate: NavigateFunction;
}): React.JSX.Element {
  return (
    <li className="border-b border-border last:border-0">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left hover:bg-bg"
        onClick={() => {
          navigate(resultHref(result));
        }}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            <Highlight text={result.title} query={query} testId={`highlight-${result.id}-title`} />
          </p>
          {result.subtitle !== undefined && (
            <p className="truncate text-xs text-muted">
              <Highlight
                text={result.subtitle}
                query={query}
                testId={`highlight-${result.id}-subtitle`}
              />
            </p>
          )}
        </div>
        {result.amountMinor !== undefined && result.currency !== undefined && (
          <Amount
            currency={result.currency}
            minorUnits={result.amountMinor}
            className="shrink-0 text-sm"
          />
        )}
      </button>
    </li>
  );
}

export function SearchPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const trimmed = query.trim();
  const { data: results, isLoading } = useSearch(query);

  const groups = GROUP_ORDER.map((kind) => ({
    kind,
    label: GROUP_LABELS[kind],
    items: (results ?? []).filter((r) => r.kind === kind),
  })).filter((g) => g.items.length > 0);

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Search</h1>

      <div className="relative mb-4 max-w-md">
        <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          type="search"
          aria-label="Search"
          className={`${inputClass} pl-9`}
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            setParams(value === '' ? {} : { q: value }, { replace: true });
          }}
          autoFocus
        />
      </div>

      {trimmed === '' && (
        <p className="text-sm text-muted">
          Search transactions, accounts, owed, third-party, and recurring — by text or amount (e.g.
          "50000", "≈500 usd").
        </p>
      )}

      {trimmed !== '' && isLoading && <p className="text-sm text-muted">Searching…</p>}

      {trimmed !== '' && !isLoading && groups.length === 0 && (
        <p className="text-sm text-muted">No results for &quot;{trimmed}&quot;.</p>
      )}

      {groups.map((group) => (
        <div
          key={group.kind}
          className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4"
        >
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
            {group.label}
          </p>
          <ul>
            {group.items.map((r) => (
              <ResultRow key={r.id} result={r} query={trimmed} navigate={navigate} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
